import React, { useEffect, useRef, useState, createContext, useContext, useCallback } from 'react';
import { AlertTriangle, Trash2, Info, HelpCircle } from 'lucide-react';

const VARIANT_CONFIG = {
  danger: { icon: Trash2, color: 'bg-red-600 hover:bg-red-700', iconBg: 'bg-red-100', iconColor: 'text-red-600' },
  warning: { icon: AlertTriangle, color: 'bg-amber-600 hover:bg-amber-700', iconBg: 'bg-amber-100', iconColor: 'text-amber-600' },
  info: { icon: Info, color: 'bg-purple-600 hover:bg-purple-700', iconBg: 'bg-purple-100', iconColor: 'text-purple-600' },
};

export function ConfirmDialog({
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'warning',
  inputPlaceholder,
  onConfirm,
  onCancel,
}) {
  const confirmRef = useRef(null);
  const [inputValue, setInputValue] = useState('');
  const config = VARIANT_CONFIG[variant] || VARIANT_CONFIG.warning;
  const IconComp = config.icon;

  useEffect(() => {
    confirmRef.current?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const handleConfirm = () => {
    if (inputPlaceholder) {
      onConfirm(inputValue);
    } else {
      onConfirm();
    }
  };

  return (
    <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/40 backdrop-blur-[1px]" onClick={onCancel} role="dialog" aria-modal="true" aria-labelledby="confirm-title">
      <div className="mx-4 w-full max-w-md rounded-xl border border-slate-200 bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3">
          <div className={`flex-shrink-0 p-2 rounded-full ${config.iconBg}`}>
            <IconComp size={20} className={config.iconColor} />
          </div>
          <div className="flex-1 min-w-0">
            <h4 id="confirm-title" className="text-sm font-bold text-slate-900">{title}</h4>
            <p className="mt-1.5 text-xs text-slate-600 leading-relaxed">{message}</p>
            {inputPlaceholder && (
              <input
                type="text"
                value={inputValue}
                onChange={e => setInputValue(e.target.value)}
                placeholder={inputPlaceholder}
                className="mt-3 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                autoFocus
              />
            )}
          </div>
        </div>
        <div className="mt-5 flex items-center justify-end gap-2">
          <button type="button" onClick={onCancel} className="rounded-lg border border-slate-200 px-4 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50 transition-colors">
            {cancelLabel}
          </button>
          <button
            ref={inputPlaceholder ? undefined : confirmRef}
            type="button"
            onClick={handleConfirm}
            disabled={inputPlaceholder && !inputValue.trim()}
            className={`rounded-lg px-4 py-2 text-xs font-semibold text-white shadow-sm transition-colors ${config.color} disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

const ConfirmContext = createContext(null);

export function ConfirmProvider({ children }) {
  const [dialogState, setDialogState] = useState(null);
  const resolveRef = useRef(null);

  const confirm = useCallback(({ title, message, confirmLabel, cancelLabel, variant, inputPlaceholder }) => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setDialogState({ title, message, confirmLabel, cancelLabel, variant, inputPlaceholder });
    });
  }, []);

  const handleConfirm = (inputValue) => {
    setDialogState(null);
    resolveRef.current?.(inputValue !== undefined ? inputValue : true);
  };

  const handleCancel = () => {
    setDialogState(null);
    resolveRef.current?.(false);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {dialogState && (
        <ConfirmDialog
          {...dialogState}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const context = useContext(ConfirmContext);
  if (!context) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('useConfirm called outside ConfirmProvider — falling back to window.confirm()');
    }
    return ({ title, message, inputPlaceholder }) => {
      if (inputPlaceholder) return Promise.resolve(window.prompt(message) || false);
      return Promise.resolve(window.confirm(message || title));
    };
  }
  return context;
}

export default ConfirmDialog;
